/**
 * TransactionsReport (FEAT-ANALYTICS-002, Monetization) — GA "Ecommerce
 * purchases"/transactions. Headline KPIs + revenue trend + revenue breakdowns
 * (product / payment method / platform) + a recent-transactions table. A
 * transaction = any money-moving event (revenue > 0), so it reads faithfully
 * for orders, donations, wallet topups and merchant payments alike.
 */
import React from 'react';
import {
  Stack, SimpleGrid, Table, Thead, Tbody, Tr, Th, Td,
  TableContainer, Badge, Tag, Button,
} from '@chakra-ui/react';
import { FiDownload } from 'react-icons/fi';
import { downloadCsv } from './exportCsv';
import { useReport } from './api';
import {
  StatCard, StatGrid, Panel, TrendChart, BreakdownCard, ReportFrame, EmptyState,
  fmtInt,
} from './components';
import { fmtMoney, soleCurrency, fmtWhen } from './txnFormat';
import { CurrencyBreakdown } from './CurrencyBreakdown';

const trendOf = (s = {}) => (s.revenue || []);
const catsOf = (s = {}) => (s.buckets || []).map((b) => String(b || '').replace(/ 00:00:00$/, '').slice(5));

export function TransactionsReport({ params }) {
  const { data, loading, error } = useReport('transactions', params);
  const k = data?.kpis || {};
  const rows = data?.transactions || [];
  // Per-currency money (DA-BUG-0455): never blend revenue across currencies.
  const byCur = data?.by_currency || [];
  const multi = byCur.length > 1;
  const single = byCur.length === 1 ? byCur[0] : null;
  const cur = single?.currency || soleCurrency(rows);
  const moneyTile = (val) => (multi ? 'By currency ↓' : fmtMoney(val, cur));
  const toMoneyRows = (arr = []) => arr.map((r) => ({ label: r.label, value: Number(r.value || 0) }));

  return (
    <ReportFrame loading={loading} error={error}>
      {data && (
        <Stack spacing={4}>
          <StatGrid columns={{ base: 2, md: 4 }}>
            <StatCard label="Total revenue" value={moneyTile((single || k).revenue)} spark={multi ? undefined : trendOf(data.series)} />
            <StatCard label="Transactions" value={fmtInt(k.transactions)} />
            <StatCard label="Buyers" value={fmtInt(k.buyers)} help="Distinct paying users" />
            <StatCard label="Avg. order value" value={moneyTile((single || k).avg_order_value)} />
          </StatGrid>

          {multi && (
            <CurrencyBreakdown
              title="Revenue by currency"
              rows={byCur}
              cols={[
                { key: 'revenue', label: 'Revenue', money: true },
                { key: 'transactions', label: 'Transactions' },
                { key: 'buyers', label: 'Buyers' },
                { key: 'avg_order_value', label: 'Avg. order value', money: true },
              ]}
            />
          )}

          <Panel title="Revenue over time">
            <TrendChart
              categories={catsOf(data.series)}
              series={[{ name: `Revenue${cur ? ` (${cur})` : ''}`, data: trendOf(data.series) }]}
            />
          </Panel>

          <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4}>
            <BreakdownCard title="Revenue by product" rows={toMoneyRows(data.byProduct)} labelHeader="Product" valueHeader="Revenue" />
            <BreakdownCard title="Revenue by payment method" rows={toMoneyRows(data.byPayment)} labelHeader="Payment method" valueHeader="Revenue" />
            <BreakdownCard title="Revenue by platform" rows={toMoneyRows(data.byChannel)} labelHeader="Platform" valueHeader="Revenue" />
          </SimpleGrid>

          <Panel
            title="Recent transactions"
            action={
              <Button size="xs" variant="outline" leftIcon={<FiDownload />} isDisabled={!rows.length}
                onClick={() => downloadCsv('transactions', [
                  { key: 'date_time', label: 'When' }, { key: 'event', label: 'Event' },
                  { key: 'user', label: 'Buyer' }, { key: 'transaction_id', label: 'Transaction' },
                  { key: 'product', label: 'Product' }, { key: 'payment_method', label: 'Payment' },
                  { key: 'currency', label: 'Currency' }, { key: 'amount', label: 'Amount' },
                ], rows)}>
                Export CSV
              </Button>
            }
          >
            {rows.length === 0 ? (
              <EmptyState label="No transactions in this period" minimal />
            ) : (
              <TableContainer>
                <Table size="sm" variant="simple">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th color="gray.500">When</Th>
                      <Th color="gray.500">Event</Th>
                      <Th color="gray.500">Buyer</Th>
                      <Th color="gray.500">Transaction</Th>
                      <Th color="gray.500">Product</Th>
                      <Th color="gray.500">Payment</Th>
                      <Th isNumeric color="gray.500">Amount</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rows.map((r, i) => (
                      <Tr key={`${r.transaction_id || i}:${i}`} _hover={{ bg: 'gray.50' }}>
                        <Td whiteSpace="nowrap" fontSize="xs" color="gray.600">{fmtWhen(r.date_time)}</Td>
                        <Td><Badge colorScheme="blue" variant="subtle" fontSize="10px">{r.event}</Badge></Td>
                        <Td fontFamily="mono" fontSize="xs" color="gray.700">{r.user}</Td>
                        <Td fontFamily="mono" fontSize="xs" color="gray.500">{r.transaction_id || '—'}</Td>
                        <Td fontSize="xs">{r.product || '—'}</Td>
                        <Td fontSize="xs">{r.payment_method ? <Tag size="sm" variant="subtle">{r.payment_method}</Tag> : '—'}</Td>
                        <Td isNumeric fontWeight="600" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {fmtMoney(r.amount, r.currency || cur)}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </Panel>
        </Stack>
      )}
    </ReportFrame>
  );
}

export default TransactionsReport;
